import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import {
  AppointmentPaymentChoice,
  type AppointmentPaymentChoiceProps,
} from '@/components/appointments/AppointmentPaymentChoice';
import { CreditNoteLinks } from '@/components/invoices/CreditNoteLinks';
import { ManualPaymentFields } from '@/components/invoices/ManualPaymentFields';
import { MobilePayRequestConfirm } from '@/components/payments/MobilePayRequestConfirm';
import { PaymentRequestList } from '@/components/payments/PaymentRequestList';
import { usePatient } from '@/hooks/usePatients';
import {
  useInvoiceMobilePayRequest,
  useMobilePayRequestGate,
} from '@/hooks/useInvoiceMobilePayRequest';
import {
  formatMoney,
  useInvoice,
  useInvoiceCreditNotes,
  useInvoicePayments,
  useRecordPayment,
} from '@/hooks/useInvoices';
import { invoiceBalanceDueCents } from '@/lib/invoiceRefund';
import {
  isPaymentRequestMethod,
  formatPaymentAmountInput,
  manualPaymentIssues,
  parsePaymentAmountCents,
  paymentDateInputToIso,
  recordedPaymentMethod,
  storedPaymentMethodLabel,
  todayDateInputValue,
} from '@/lib/manualPaymentMethods';
import { mobilePayRequestBlock } from '@/lib/mobilePayRequest';
import { canOfferPaymentRequest } from '@/lib/paymentRequests';
import { CLINIC_TIMEZONE, formatZonedDate } from '@/lib/reporting/timezone';
import { uiDateLocale } from '@/lib/i18n';
import { UI_MUTED } from '@/lib/typography';

export interface PaymentDialogProps {
  presentation?: 'embedded' | 'modal';
  onClose?: () => void;
  clinicId: string;
  invoiceId?: string | null;
  appointment?: AppointmentPaymentChoiceProps | null;
  audience?: 'staff' | 'patient';
  canRecordManualPayment?: boolean;
  onCancel?: () => void;
}

export function PaymentDialog({
  presentation = 'embedded',
  onClose,
  clinicId,
  invoiceId,
  appointment,
  audience = 'staff',
  canRecordManualPayment = true,
  onCancel,
}: PaymentDialogProps) {
  const { t } = useTranslation();
  const body = (
    <div data-payment-dialog="">
      {invoiceId ? (
        <InvoiceSettlementPanel
          clinicId={clinicId}
          invoiceId={invoiceId}
          audience={audience}
          canRecordManualPayment={canRecordManualPayment}
          onCancel={onCancel}
        />
      ) : appointment ? (
        <AppointmentPaymentChoice {...appointment} embedded />
      ) : null}
    </div>
  );

  if (presentation === 'modal') {
    return (
      <Modal title={t('appointments.details.payment_action', 'Betaling')} onClose={onClose ?? (() => undefined)}>
        {body}
      </Modal>
    );
  }
  return body;
}

function providerPayment(method: string | null, recordedBy: string | null, notes: string | null): boolean {
  const key = (method ?? '').toLowerCase();
  if (key !== 'card' && key !== 'mobilepay' && key !== 'clinio_pay') return false;
  if (notes?.toLowerCase().includes('clinio pay')) return true;
  return recordedBy == null && key === 'clinio_pay';
}

function InvoiceSettlementPanel({
  clinicId,
  invoiceId,
  audience,
  canRecordManualPayment,
  onCancel,
}: {
  clinicId: string;
  invoiceId: string;
  audience: 'staff' | 'patient';
  canRecordManualPayment: boolean;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const { data: creditNotes, isLoading: creditNotesLoading } = useInvoiceCreditNotes(invoiceId);
  const { data: payments } = useInvoicePayments(invoiceId);
  const { data: patient } = usePatient(invoice?.patient_id);
  const recordPayment = useRecordPayment();
  const requestMobilePay = useInvoiceMobilePayRequest();
  const mobilePayGate = useMobilePayRequestGate(clinicId);
  const [method, setMethod] = useState('');
  const [otherDetail, setOtherDetail] = useState('');
  const [amount, setAmount] = useState<string | null>(null);
  const [paymentDate, setPaymentDate] = useState(() => todayDateInputValue());
  const [mobilePayConfirm, setMobilePayConfirm] = useState(false);
  const [fieldError, setFieldError] = useState<{
    method?: string;
    other?: string;
    amount?: string;
    date?: string;
  }>({});

  if (isLoading || !invoice) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-ds-muted" />
      </div>
    );
  }

  const paid = invoice.total_paid_cents ?? 0;
  const creditedCents = (creditNotes ?? []).reduce((sum, note) => sum + note.total_cents, 0);
  const remaining = invoiceBalanceDueCents({
    status: invoice.status,
    totalCents: invoice.total_cents,
    paidCents: paid,
    creditedCents,
  });
  const amountValue = amount ?? formatPaymentAmountInput(remaining);
  const locale = uiDateLocale();
  const offerActions =
    !creditNotesLoading &&
    invoice.status !== 'refunded' &&
    audience === 'staff' &&
    canOfferPaymentRequest({ outstandingCents: remaining, status: invoice.status });
  const canRegister = offerActions && canRecordManualPayment;

  function issueMessage(issue: ReturnType<typeof manualPaymentIssues>[number]): string {
    switch (issue) {
      case 'method':
        return t('invoices.payment_method_required');
      case 'other':
        return t('invoices.payment_method_other_required');
      case 'amount_required':
        return t('invoices.payment_amount_required');
      case 'amount_invalid':
        return t('invoices.payment_invalid_amount');
      case 'date_required':
        return t('invoices.payment_date_required');
      case 'date_future':
        return t('invoices.payment_date_invalid');
      default: {
        const unexpected: never = issue;
        return unexpected;
      }
    }
  }

  async function submitManual(event: FormEvent) {
    event.preventDefault();
    if (isPaymentRequestMethod(method)) {
      setFieldError({});
      setMobilePayConfirm(true);
      return;
    }
    const issues = manualPaymentIssues({
      method,
      otherDetail,
      amount: amountValue,
      paymentDate,
      remainingCents: remaining,
    });
    if (issues.length > 0) {
      setFieldError({
        method: issues.includes('method') ? issueMessage('method') : undefined,
        other: issues.includes('other') ? issueMessage('other') : undefined,
        amount: issues.includes('amount_required')
          ? issueMessage('amount_required')
          : issues.includes('amount_invalid')
            ? issueMessage('amount_invalid')
            : undefined,
        date: issues.includes('date_required')
          ? issueMessage('date_required')
          : issues.includes('date_future')
            ? issueMessage('date_future')
            : undefined,
      });
      return;
    }
    const recordedMethod = recordedPaymentMethod(method, otherDetail);
    if (!recordedMethod) return;
    setFieldError({});
    const amountCents = parsePaymentAmountCents(amountValue) ?? 0;
    try {
      await recordPayment.mutateAsync({
        invoice_id: invoiceId,
        amount_cents: amountCents,
        payment_date: paymentDateInputToIso(paymentDate),
        payment_method: recordedMethod,
      });
      toast.success(t('invoices.payment_recorded'));
      setMethod('');
      setOtherDetail('');
      setAmount(null);
      setPaymentDate(todayDateInputValue());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('invoices.payment_error'));
    }
  }

  const patientName =
    `${patient?.first_name ?? ''} ${patient?.last_name ?? ''}`.trim() || invoice.patient_id;
  const mobilePayBlock = mobilePayRequestBlock({
    ready: mobilePayGate.ready,
    mobilePayEnabled: mobilePayGate.mobilePayEnabled,
    clinioPayAvailable: mobilePayGate.clinioPayAvailable,
    outstandingCents: remaining,
  });
  async function sendMobilePayRequest() {
    try {
      await requestMobilePay.mutateAsync(invoiceId);
      setMobilePayConfirm(false);
      setMethod('');
      toast.success(t('appointments.payment_choice.mobilepay_sent'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('appointments.payment_choice.errors.mobilepay_failed'));
    }
  }

  return (
    <div className="space-y-4" data-invoice-settlement="">
      <dl className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <dt className={UI_MUTED}>{t('invoices.total_amount', 'Total')}</dt>
          <dd>{formatMoney(invoice.total_cents, invoice.currency)}</dd>
        </div>
        <div>
          <dt className={UI_MUTED}>{t('invoices.amount_paid', 'Betalt')}</dt>
          <dd>{formatMoney(paid, invoice.currency)}</dd>
        </div>
        <div>
          <dt className={UI_MUTED}>{t('invoices.remaining_balance', 'Udestående')}</dt>
          <dd>{formatMoney(remaining, invoice.currency)}</dd>
        </div>
      </dl>

      {invoice.status === 'refunded' ? (
        <div data-refunded-invoice="">
          <p className="text-sm text-ds-text">{t('invoices.status_refunded')}</p>
          <p className="text-sm text-ds-muted">{t('invoices.refunded_settled')}</p>
          <CreditNoteLinks notes={creditNotes ?? []} />
        </div>
      ) : (
        <CreditNoteLinks notes={creditNotes ?? []} />
      )}

      <PaymentRequestList invoiceId={invoiceId} />

      {offerActions ? (
        <section className="space-y-3" data-payment-actions="">
          {canRegister ? (
            <form
              className="space-y-2"
              noValidate
              data-manual-payment=""
              data-settlement-type="invoice_payment"
              id={`invoice-pay-${invoiceId}`}
              onSubmit={submitManual}
            >
              <Input
                id={`invoice-pay-amount-${invoiceId}`}
                label={t('invoices.payment_amount', 'Beløb')}
                type="text"
                inputMode="decimal"
                value={amountValue}
                error={fieldError.amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  setFieldError((current) => ({ ...current, amount: undefined }));
                }}
              />
              <ManualPaymentFields
                idPrefix={`invoice-payment-${invoiceId}`}
                method={method}
                onMethodChange={(next) => {
                  setMethod(next);
                  setFieldError((current) => ({ ...current, method: undefined, other: undefined }));
                  if (isPaymentRequestMethod(next)) setMobilePayConfirm(true);
                }}
                methodError={fieldError.method}
                otherDetail={otherDetail}
                onOtherDetailChange={(value) => {
                  setOtherDetail(value);
                  setFieldError((current) => ({ ...current, other: undefined }));
                }}
                otherError={fieldError.other}
                paymentDate={paymentDate}
                onPaymentDateChange={(value) => {
                  setPaymentDate(value);
                  setFieldError((current) => ({ ...current, date: undefined }));
                }}
                dateError={fieldError.date}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  disabled={recordPayment.isPending || requestMobilePay.isPending}
                >
                  {isPaymentRequestMethod(method)
                    ? t('invoices.mobilepay_request_send')
                    : t('invoices.save_payment')}
                </Button>
                {onCancel ? (
                  <Button type="button" variant="secondary" onClick={onCancel}>
                    {t('common.cancel')}
                  </Button>
                ) : null}
              </div>
            </form>
          ) : null}
          {mobilePayConfirm && (
            <MobilePayRequestConfirm
              amountLabel={formatMoney(remaining, invoice.currency)}
              patientName={patientName}
              block={mobilePayBlock}
              ready={mobilePayGate.ready}
              busy={requestMobilePay.isPending}
              onCancel={() => {
                setMobilePayConfirm(false);
                setMethod('');
              }}
              onConfirm={() => {
                void sendMobilePayRequest();
              }}
            />
          )}
        </section>
      ) : null}

      <section>
        <h6 className="text-sm font-medium text-ds-text">
          {t('invoices.payment_history', 'Betalinger')}
        </h6>
        <ul className="mt-2 space-y-1 text-sm">
          {(payments ?? []).map((payment) => {
            const fromProvider = providerPayment(
              payment.payment_method,
              payment.recorded_by,
              payment.notes,
            );
            return (
              <li key={payment.id} data-payment-row="">
                {storedPaymentMethodLabel(payment.payment_method, t)} ·{' '}
                {formatMoney(payment.amount_cents, payment.currency)} ·{' '}
                {formatZonedDate(payment.payment_date, locale, CLINIC_TIMEZONE)}
                {fromProvider
                  ? ` · ${t('appointments.payment_dialog.via_clinio_pay', 'Betalt via Clinio Pay')}`
                  : ''}
              </li>
            );
          })}
          {(payments ?? []).length === 0 && (
            <li className={UI_MUTED}>{t('appointments.payment_dialog.no_payments', 'Ingen betalinger endnu')}</li>
          )}
        </ul>
      </section>
    </div>
  );
}
